import React, {useMemo, useCallback, useRef, useEffect} from 'react';
import {useNotificationContext} from 'contexts';
import Excel from 'shared/Excel';
import useDynamicValues from 'components/customer/Preview3D/values/useDynamicValues';
import nunjucks from 'nunjucks';
import {
    ShelfType,
    PreviewFormValues,
    PreviewLocation,
    Drawer,
} from 'components/customer/Preview3D/types';
import useAdjustableLegs from 'components/customer/Preview3D/hardware/useAdjustableLegs';
import useShapedPanel from 'components/customer/Preview3D/hardware/useShapedPanel';
import {hasLShapeFootprint} from 'components/customer/Preview3DCommon/helpers/hasLShapeFootprint';
import * as THREE from 'three';
import useMaterialTexture from 'components/customer/Preview3D/lib/useMaterialTexture';
import {use3DContext} from 'components/customer/Preview3D/components/Preview3DComponent';
import {Cabinet} from 'components/customer/Product/entity/Cabinet';

export type TemplateVariable = {
    fields: {
        name: string;
        expression: string;
    }[];
    shelfType?: ShelfType;
    isLeftReturn?: boolean;
    innerDrawerCount?: number;
    isDrawerFace?: boolean;
    hasLowerShelf?: boolean;
    isBroom2Part?: boolean;
    hasToeKick?: boolean;
    noInteractions?: boolean;
    hasLShapedShelves?: boolean;
    isReturnProduct?: boolean;
    isLShapePanel?: boolean;
    isBinWithInnerDrawerProduct?: boolean;
    isBinProduct?: boolean;
    hasRangehoodVent?: boolean;
    hasInsetDrawer?: boolean;
    isCornerProduct?: boolean;
    isUShapedPanel?: boolean;
    hasCornerShapedShelves?: boolean;
    noCornerBaseBottom?: boolean;
    is2Part?: boolean;
    isUpperProduct?: boolean;
    isClipProduct?: boolean;
    isHighSwing?: boolean;
    isHighLift?: boolean;
    isLeftClip?: boolean;
    isRadiusProduct?: boolean;
    isLeftRadius?: boolean;
    isWallOvenProduct?: boolean;
    isLShapedPantry?: boolean;
    isApplianceBifoldProduct?: boolean;
    isApplianceLeftBifoldRight?: boolean;
    isApplianceLeftRightBifold?: boolean;
    isApplianceBifoldPair?: boolean;
    isApplianceProduct?: boolean;
    isOpenLShape?: boolean;
    is3DoorProduct?: boolean;
    isKeyShapePanel?: boolean;
    hasBottomVent?: boolean;
    cutoutWidth?: number;
    inset?: number;
    hasUpperPairCabinet?: boolean;
    isLsSolidTop?: boolean;
    isCabinetUsingExterior?: boolean;
    drawerWidthOffset?: number;
    runnerOffset?: number;
    isLShapeDrawerProduct?: boolean;
    hasDrillings?: boolean;
    innerDrawerOnly?: boolean;
    hasUpperLiftSystem?: boolean;
    isFoldKitProduct?: boolean;
    isFoldKitLeftReturn?: boolean;
    // Added to replace magic numbers in renderCabinetGroup.ts
    hasWidthOffset?: boolean; // Replaces [37, 70, 87, 354, 227, 228, 79, 40, 38, 10, 80, 85, 229, 230, 353]
    isNonManualPanel?: boolean; // Replaces ['85', '229', '230', '353']
    isKickerLengthProduct?: boolean; // Replaces ['85', '229', '230']
    isPanelProduct?: boolean; // Replaces cabinetId == 37
    isDoorStylePanel?: boolean; // Door, Panel and Applied Panels take the door style
};

export type ProductDataStore = React.MutableRefObject<{
    template_3d?: {
        attributes?: {
            variables?: string;
            template?: string;
            cabinet_id?: number;
        };
    }[];
    cabinet?: {
        attributes?: Cabinet;
    };
    drawers?: Drawer[];
}>;

type DynamicValues<T> = Record<string, string | number | boolean | T>;

const usePreview3DData = (
    scene: React.MutableRefObject<THREE.Scene>,
    showTexture?: boolean,
    previewLocation?: PreviewLocation,
    cabinetGroupRef?: React.MutableRefObject<THREE.Group | null>
) => {
    const {values, productDataStore} = use3DContext();
    const {messages} = useNotificationContext();

    const template3d = productDataStore.current?.template_3d;
    const content3d =
        Array.isArray(template3d) && template3d.length ? template3d[0] : null;

    const variables = JSON.parse(
        content3d?.attributes?.variables
    ) as TemplateVariable;
    const templateData: string = content3d?.attributes?.template;
    const productType = String(content3d?.attributes?.cabinet_id);

    const {exteriorThickness, carcaseThickness} = useMaterialTexture();

    const {adjustableLegsParts} = useAdjustableLegs(
        scene,
        variables.isLeftReturn,
        hasLShapeFootprint(variables),
        variables?.hasBottomVent
            ? {
                  front: variables.inset,
                  topLeft: variables.inset,
                  topRight: variables.inset,
                  back: variables.cutoutWidth,
              }
            : undefined,
        cabinetGroupRef,
        carcaseThickness
    );

    const hasError = useRef(false);

    const fieldDependencies = useMemo(() => {
        const fieldNames = variables.fields
            .map((f) => {
                if (values.hasOwnProperty(f.expression)) return f.expression;

                return '';
            })
            .filter((f) => f);

        return [
            ...fieldNames.map((field) => {
                return {
                    [String(field)]: values[String(field)] as PreviewFormValues,
                };
            }),
        ];
    }, [values]);

    const {dynamicValues} = useDynamicValues(variables?.shelfType, [
        ...fieldDependencies,
        messages.errors,
    ]);

    const {shapedParts} = useShapedPanel(scene, showTexture, previewLocation);

    const replaceVariables = (
        jsonString: string,
        replacements: DynamicValues<
            (typeof dynamicValues)[keyof typeof dynamicValues]
        >
    ): string => {
        const renderedTemplate = nunjucks.renderString(
            jsonString,
            replacements
        );

        return renderedTemplate;
    };

    const replaceDimensions = useCallback(
        (json: string) => {
            const formValues: DynamicValues<
                (typeof dynamicValues)[keyof typeof dynamicValues]
            > = {
                ...dynamicValues,
            };

            variables.fields.forEach((f) => {
                const evaluated = Excel.calculate(f.expression, {
                    ...values,
                    exteriorThickness,
                    carcaseThickness,
                });
                formValues[f.name] = evaluated;
            });

            return replaceVariables(json, {...formValues});
        },
        [...fieldDependencies, messages.errors]
    );

    const setupHardware = () => {
        const parts = [...adjustableLegsParts];

        for (let index = 0; index < parts.length; index++) {
            const {condition, callback} = parts[Number(index)];

            if (condition) callback();
        }
    };

    const getShapedPanels = () => {
        const parts = [...shapedParts];

        const meshes: THREE.Mesh[] = [];

        for (let index = 0; index < parts.length; index++) {
            const {condition, callback} = parts[Number(index)];

            if (condition) {
                const result = callback();

                if (result) {
                    meshes.push(...result);
                }
            }
        }

        return meshes;
    };

    useEffect(() => {
        hasError.current =
            messages.errors &&
            Boolean((messages.errors as {fields: string[]}[])?.length);
    }, [messages.errors]);

    return {
        variables,
        fieldDependencies,
        replaceVariables,
        replaceDimensions,
        templateData,
        setupHardware,
        hasError,
        errors: messages.errors,
        getShapedPanels,
        productType,
    };
};

export default usePreview3DData;
